import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { coursesApi } from "../api/courses.api"
import type { CourseFilters, CreateCourseFormData, UpdateCourseFormData } from "../types"

const COURSES_KEY = ["courses"] as const

export function useCourses(filters: CourseFilters = {}) {
  return useQuery({
    queryKey: [...COURSES_KEY, filters],
    queryFn: () => coursesApi.fetchAll(filters).then((r) => r.data),
  })
}

export function useCourse(id: string) {
  return useQuery({
    queryKey: [...COURSES_KEY, id],
    queryFn: () => coursesApi.fetchOne(id).then((r) => r.data.data),
    enabled: !!id,
  })
}

export function useCreateCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCourseFormData) => coursesApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: COURSES_KEY }),
  })
}

export function useUpdateCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCourseFormData }) =>
      coursesApi.update(id, data),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: [...COURSES_KEY, id] })
      qc.invalidateQueries({ queryKey: COURSES_KEY })
    },
  })
}

export function useDeleteCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => coursesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: COURSES_KEY }),
  })
}

export function usePublishCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => coursesApi.publish(id),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: [...COURSES_KEY, id] })
      qc.invalidateQueries({ queryKey: COURSES_KEY })
    },
  })
}

export function useArchiveCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => coursesApi.archive(id),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: [...COURSES_KEY, id] })
      qc.invalidateQueries({ queryKey: COURSES_KEY })
    },
  })
}
